/**
 * Meta Conversions API (CAPI) – Client-Side Dispatcher
 *
 * Sends server-side events to the Meta CAPI via our edge function.
 * Each event carries an `event_id` that is also passed to the browser
 * pixel so Meta can deduplicate the pair.
 *
 * This module is consent-aware: it only fires when the Meta Pixel has been
 * initialised (which itself requires marketing consent).
 */

import { isPixelInitialised } from './metaPixel';
import { getMarketingConsent } from './consent';
import { getMetaTestEventCode } from './debug';
import { META_PIXEL_ID, CAPI_ENDPOINT } from './config';
import { getAnalyticsUserData } from './userData';

/** Re-export for backward compat — prefer importing from '@/lib/analytics' */
export { generateEventId } from './config';

// ── Browser-level user data collection ──────────────────────────────────────

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Collect browser-level Meta matching parameters for every CAPI event.
 * - fbp: Meta browser ID cookie (_fbp)
 * - fbc: Meta click ID cookie (_fbc) or constructed from fbclid URL param
 */
function collectBrowserUserData(): Record<string, unknown> {
  const data: Record<string, unknown> = {};

  const fbp = getCookie('_fbp');
  const fbc = getCookie('_fbc');
  if (fbp) data.fbp = fbp;
  if (fbc) data.fbc = fbc;

  // If fbc not in cookie, try URL click ID
  if (!fbc && typeof window !== 'undefined') {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) {
      data.fbc = `fb.1.${Date.now()}.${fbclid}`;
    }
  }

  return data;
}

interface ServerEvent {
  event_name: string;
  event_id: string;
  event_source_url?: string;
  user_data?: Record<string, unknown>;
  custom_data?: Record<string, unknown>;
}

/**
 * Send one or more events to the CAPI edge function.
 * Fires asynchronously – failures are logged but never block the UI.
 */
export async function sendServerEvents(events: ServerEvent[]): Promise<void> {
  if (getMarketingConsent() !== 'granted') {
    if (import.meta.env.DEV) console.debug('[CAPI] Skipped: consent not granted');
    return;
  }
  if (!isPixelInitialised()) {
    if (import.meta.env.DEV) console.debug('[CAPI] Skipped: pixel not initialised');
    return;
  }
  if (!META_PIXEL_ID || !CAPI_ENDPOINT) {
    console.warn('[CAPI] Skipped: missing META_PIXEL_ID or CAPI_ENDPOINT', { META_PIXEL_ID: !!META_PIXEL_ID, CAPI_ENDPOINT: !!CAPI_ENDPOINT });
    return;
  }

  const url = CAPI_ENDPOINT;

  const testCode = getMetaTestEventCode();

  // Collect browser-level matching params for every event
  const browserUserData = collectBrowserUserData();

  const payload: Record<string, unknown> = {
    pixel_id: META_PIXEL_ID,
    ...(testCode ? { test_event_code: testCode } : {}),
    events: events.map((e) => ({
      event_name: e.event_name,
      event_id: e.event_id,
      event_time: Math.floor(Date.now() / 1000),
      event_source_url: e.event_source_url ?? (typeof window !== 'undefined' ? window.location.href : undefined),
      action_source: 'website' as const,
      user_data: {
        client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        ...browserUserData,
        ...getAnalyticsUserData(),
        ...(e.user_data ?? {}),
      },
      custom_data: e.custom_data ?? {},
    })),
  };

  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Fire-and-forget: use keepalive so the request survives page navigations
      keepalive: true,
    });
  } catch (err) {
    console.warn('[CAPI] Failed to send server events:', err);
  }
}

/**
 * Convenience: send a single event to CAPI.
 */
export function sendServerEvent(event: ServerEvent): Promise<void> {
  return sendServerEvents([event]);
}
